/**
 * 配置済みの業務フロー図を、React Flow が受け取るノードとエッジへ組み立てる。
 *
 * 線の割り当ては 2026-09-21 の図（`d2a1aab38` の `user-story-flow-build.tsx`）の規則に揃えてある。
 * 水路・縦線・上がる位置を**図 1 枚ぶんまとめて**、区間が重ならない線どうしで位置を共有させて
 * 割り当て、分岐から出る枝を色で塗り分け、結末のラベルを節点・札・ほかのラベル・水平な線の
 * どれにも重ならない位置へ逃がす。1 本ずつ決めると、離れた線まで同じ位置を取り合い、ラベルが
 * 線と節点の上に乗る。
 *
 * 宛先 4 種を描き分ける。線はどれも実線で、次へ ＝ 次の節点へ、戻る ＝ 升目の下を回る線で「戻る」の語は
 * 指すと出す、止まる ＝ 終了の節点か「止まる」の札へ、出口へ ＝「出口 → 行き先」の札へ入れる。
 *
 * **破線は「案件で消えた」にだけ使う**（`flow-edge-removed`、`styles.css`）。止まる・出口へまで
 * 破線にすると、同じ見た目に 2 つの意味が乗り、差分と関係ない線が消えたように見える。宛先の違いは
 * 線の種類ではなく、線が入る先（終了の節点・札）で見分ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 配置済みの業務フロー図を、作図規約に沿った経路・分岐の枝の色・重ならないラベル・差分の印つきで React Flow のノードとエッジへ組み立てる。
 * @graph-connects buildEdgePath [calls] 作図規約に沿った経路の組み立て
 * @graph-connects packLanes [calls] 重ならない区間で位置を共有する割り当て
 */
import type { FlowNodeView } from '@cortex/domain-spec-console';
import { MarkerType, Position, type Edge, type Node } from '@xyflow/react';
import type { CSSProperties, ReactNode } from 'react';

import type { FlowLaneLabel } from '../flow-workspace/flow-canvas.js';
import {
  ADDED_EDGE_CLASS,
  REMOVED_EDGE_CLASS,
  type FlowChangeState,
} from '../flow-workspace/flow-diff-marks.js';
import { FLOW_EDGE_TYPE } from '../FlowGutterEdge.js';
import { GUTTER_WITH_LABEL, NODE_HEIGHT } from './constants.js';
import {
  buildEdgePath,
  channelY,
  departureY,
  edgeLabelPosition,
  entryX,
  gutterX,
  packLanes,
  resolveEdgeLabelPositions,
} from './edges.js';
import {
  UNASSIGNED_LANE_ID,
  type BusinessFlowLayout,
  type LaneRow,
  type PlacedDiagramNode,
} from './layout.js';
import {
  branchColorOf,
  FlowNodeContent,
  flowNodeLabelOf,
  FlowTagContent,
  footprintHeightOf,
  shapeInsetOf,
  shapeWidthOf,
  type FlowNodeLabels,
} from './nodes.js';

/** 既定ノード型が持つ枠・余白を消す。中身が自分で枠を描くため。 @graph-connects none */
const NODE_STYLE_RESET: CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: 0,
};

/**
 * レーン帯の重ね順。エッジ層より背面に置くための負値である。
 *
 * 帯は不透明で全幅なので、既定の重ね順（0）で置くとエッジを覆い隠して「線が 1 本も無い図」に
 * なる。帯は背景の装飾なので、エッジより後ろに落とす。
 * @graph-connects none
 */
const LANE_Z_INDEX = -1;

/** レーン帯のスタイル。帯は背景の装飾だけで、レーン名は縮尺の外の層（`FlowCanvas`）が描く。 @graph-connects none */
const LANE_STYLE: CSSProperties = {
  padding: 0,
  borderRadius: 0,
  borderWidth: '1px 0 0 0',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  background: 'var(--color-bg-base)',
};

/**
 * レーン帯を React Flow のノードとして組み立てる。帯は背景だけで、名前は載せない。
 *
 * 幅を引数で受け取るのは、左右 2 枚の比較で帯の幅を揃えるためである。
 *
 * @param lanes レーン帯
 * @param width 図全体の幅
 * @returns React Flow のノード
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図のレーン帯を、背景の帯として React Flow のノードに組み立てる。
 * @graph-connects none
 */
export function buildLaneNodes(lanes: readonly LaneRow[], width: number): Node[] {
  return lanes.map((lane) => ({
    id: `lane:${String(lane.laneId)}`,
    position: { x: 0, y: lane.y },
    data: { label: null },
    style: { ...LANE_STYLE, width, height: lane.height },
    draggable: false,
    selectable: false,
    zIndex: LANE_Z_INDEX,
  }));
}

/** レーン帯の見出しに出す語。 */
export interface LaneLabelWords {
  /** アクターを引けない帯の見出し */
  unassigned: string;
  /** アクターは引けるが、その名前が空の帯の見出し */
  unset: string;
  /** 訳が入っていない印 */
  untranslated: string;
}

/**
 * レーン名を、縮尺の外で枠の左端に留めて描く並びとして組み立てる。
 *
 * **名前が空の帯は「未設定」と出す。** アクターは引けているのに名前が空の帯を「アクター未記入」と
 * 出すと、アクターが結び付いていない帯と見分けが付かない。直す場所が違う。
 *
 * @param lanes レーン帯
 * @param words 帯の見出しに出す語
 * @returns レーン名の並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図のレーン名を、アクター未記入・名前が未設定・訳なしを見分けて組み立てる。
 * @graph-connects none
 */
export function buildLaneLabels(lanes: readonly LaneRow[], words: LaneLabelWords): FlowLaneLabel[] {
  return lanes.map((lane) => ({
    key: `lane:${String(lane.laneId)}`,
    y: lane.y,
    height: lane.height,
    label: (
      <span data-story-lane={lane.laneId}>
        {lane.laneId === UNASSIGNED_LANE_ID
          ? words.unassigned
          : lane.label === ''
            ? words.unset
            : lane.label}
        {lane.untranslated && (
          <span className="ml-1 text-[var(--color-text-muted)]">{words.untranslated}</span>
        )}
      </span>
    ),
  }));
}

/** `buildDiagramNodes` の選択・差分・語の指定。 */
export interface DiagramNodeBuildOptions {
  /** 節点ごとの差分状態。差分の無い節点は持たない */
  stateByNodeId: ReadonlyMap<number, FlowChangeState>;
  /** 詳細ペインで開いている節点の局所 ID */
  selectedNodeId: number | null;
  /** 業務の節点を押したときに伝える（開始・終了・菱形は押せない） */
  onSelect: (nodeId: number) => void;
  /** 節点の中身に出す語 */
  labels: FlowNodeLabels;
  /** 節点を指したときに出す文（開始の入口の文・菱形の並列の説明）。無ければ空文字 */
  hintOf: (node: FlowNodeView) => string;
  /** 札の種類ごとの中身 */
  tagContent: (kind: 'exit' | 'stop') => ReactNode;
}

/**
 * 配置済みの節点と札を React Flow のノードとして組み立てる。
 *
 * **出口を右・入口を左に固定する。** React Flow の既定は「入口が上・出口が下」で、横へ並べた節点を
 * 繋ぐと線が一度下へ出て回り込む。
 *
 * @param placed 配置済みの節点と札
 * @param options 選択・差分・語
 * @returns React Flow のノード
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 配置済みの業務フロー図の節点と札を、種別ごとの形・選択状態・差分の印つきで React Flow のノードへ組み立てる。
 * @graph-connects FlowNodeContent [calls] 節点の形
 * @graph-connects FlowTagContent [calls] 札の形
 */
export function buildDiagramNodes(
  placed: readonly PlacedDiagramNode[],
  options: DiagramNodeBuildOptions,
): Node[] {
  return placed.map((entry): Node => {
    const { item } = entry;
    const flowNode = item.flowNode;
    const label =
      flowNode === null ? (
        <FlowTagContent
          kind={item.shape === 'exitTag' ? 'exit' : 'stop'}
          cellWidth={entry.cellWidth}
        >
          {options.tagContent(item.shape === 'exitTag' ? 'exit' : 'stop')}
        </FlowTagContent>
      ) : (
        <FlowNodeContent
          node={flowNode}
          heading={flowNodeLabelOf(flowNode, options.labels)}
          cellWidth={entry.cellWidth}
          selected={flowNode.nodeId === options.selectedNodeId}
          changeState={options.stateByNodeId.get(flowNode.nodeId) ?? null}
          hint={options.hintOf(flowNode)}
          onSelect={() => {
            options.onSelect(flowNode.nodeId);
          }}
        />
      );
    return {
      id: item.key,
      position: { x: entry.x, y: entry.y },
      data: { label },
      style: { ...NODE_STYLE_RESET, width: entry.cellWidth },
      draggable: false,
      selectable: flowNode?.kind === 'business',
      zIndex: 1,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  });
}

/** 2 つの値が作る区間。順序は問わない。 @graph-connects none */
function spanOf(a: number, b: number): { from: number; to: number } {
  return { from: Math.min(a, b), to: Math.max(a, b) };
}

/**
 * 同じ群（段・ガター）の中だけで位置を詰め、線ごとの座標へ変換する。
 *
 * 群を跨いで詰めると、離れた段やガターの線が位置を取り合って位置の数が無駄に増える。群ごとに独立
 * して詰めれば、同じ群の中で区間が重なる線だけが別の位置を取る。座標へ変換するとき、その群で実際に
 * 使った位置の数も渡す。帯の広さは決まっているので、何本を収めるのかが分からないと刻みを決められない。
 *
 * @param items 線
 * @param groupOf 線の群
 * @param intervalOf 線が占める区間
 * @param coordinateOf 位置番号から座標への変換
 * @returns 線ごとの座標
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 線の位置を群ごとに詰め、重ならない線どうしで同じ位置を共有させる。
 * @graph-connects packLanes [calls] 区間の重なりを避けた位置番号の割り当て
 */
function assignByGroup<T>(
  items: readonly T[],
  groupOf: (item: T) => number,
  intervalOf: (item: T, index: number) => { from: number; to: number },
  coordinateOf: (item: T, lane: number, laneCount: number) => number,
): number[] {
  const groups = new Map<number, number[]>();
  items.forEach((item, index) => {
    const key = groupOf(item);
    const bucket = groups.get(key) ?? [];
    bucket.push(index);
    groups.set(key, bucket);
  });

  const lanes = new Array<number>(items.length).fill(0);
  const counts = new Array<number>(items.length).fill(1);
  for (const indices of groups.values()) {
    const assigned = packLanes(indices.map((index) => intervalOf(items[index], index)));
    const used = Math.max(...assigned) + 1;
    indices.forEach((index, position) => {
      lanes[index] = assigned[position];
      counts[index] = used;
    });
  }

  return items.map((item, index) => coordinateOf(item, lanes[index], counts[index]));
}

/** `buildDiagramEdges` の差分の指定と語。 */
export interface DiagramEdgeBuildOptions {
  /** 印を付ける枝の ID */
  markedTransitionIds: ReadonlySet<string>;
  /** 印の種類（追加か削除か） */
  markedKind: 'added' | 'removed' | 'none';
  /** 戻る線を指したときに出す語 */
  backWord: string;
  /** 節点の表示名（ラベルが避ける節点の高さの見積りに使う） */
  labels: FlowNodeLabels;
}

/**
 * 線 1 本の見出し（結末の行）。見出しの無い線は null。
 *
 * 戻る線に「戻る」の語を頭に付けない（Dhik 2026-09-29「テキスト量が多いことが課題」）。升目の下を
 * 回る形で戻ることは分かるので、語は線を指したときに出す（`title`）。
 * @graph-connects none
 */
function edgeLabelOf(label: string): string | null {
  return label === '' ? null : label;
}

/**
 * 線を React Flow のエッジとして組み立てる。
 *
 * 経路は作図規約の実装（`edges.ts`）が決める。ここが渡すのは「どこからどこへ、その分岐の何本目か」
 * だけで、線の形は持たない。**位置の割り当ては図 1 枚ぶんをまとめて解く。** 1 本ずつ順に番号を
 * 振ると、離れた場所を走る線まで別々の位置を取って在庫が尽き、尽きた線が同じ位置へ重なる。
 *
 * **差分の印が付く線は枝の色を持たない。** 差分の色と枝の色が同じ線で競合する。
 *
 * @param layout 配置
 * @param options 差分の印と語
 * @returns React Flow のエッジ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの枝を、作図規約に沿った経路・分岐の枝の色・重ならない結末のラベル・宛先の描き分け・差分の印つきで React Flow のエッジへ組み立てる。
 * @graph-connects buildEdgePath [calls] 作図規約に沿った直交パスの組み立て
 * @graph-connects edgeLabelPosition [calls] ラベルの初期位置の決定
 * @graph-connects resolveEdgeLabelPositions [calls] ラベルの衝突解決
 * @graph-connects gutterX [calls] ガター内の縦線 X の決定
 * @graph-connects channelY [calls] 横に走る水路の Y の決定
 * @graph-connects entryX [calls] 終点手前で上がり直す X の決定
 * @graph-connects departureY [calls] 同じ節点から出る線の根元の高さ
 * @graph-connects branchColorOf [calls] 分岐の枝の色の決定
 */
export function buildDiagramEdges(
  layout: BusinessFlowLayout,
  { markedTransitionIds, markedKind, backWord, labels }: DiagramEdgeBuildOptions,
): Edge[] {
  const placedByKey = new Map(layout.nodes.map((entry) => [entry.item.key, entry]));

  const routes = layout.graph.edges.flatMap((edge) => {
    const source = placedByKey.get(edge.from);
    const target = placedByKey.get(edge.to);
    /* 端点が図に無い線は描けない。React Flow は端点の見つからないエッジを警告だけ出して落とす。 */
    if (source === undefined || target === undefined) return [];
    /* 根元をずらす量と枝の色は、同じ始点から出る線の本数と何本目かで決まる。 */
    const siblings = layout.graph.edges.filter((other) => other.from === edge.from);
    const order = siblings.indexOf(edge);
    const outgoingTotal = siblings.length;
    return [
      {
        edge,
        source,
        target,
        order,
        outgoingTotal,
        /* 線は図形の縁から出て縁へ入る。升目より狭い図形では升目の中で寄せた位置になる。 */
        startX:
          source.x +
          shapeInsetOf(source.item.shape, source.cellWidth) +
          shapeWidthOf(source.item.shape),
        endX: target.x + shapeInsetOf(target.item.shape, target.cellWidth),
        /* 隣かどうかは列番号ではなく座標で決まる。列の幅が一定ではないため。 */
        adjacent: target.x === source.x + source.cellWidth + source.gutterWidth,
        startY: departureY(source.y + NODE_HEIGHT / 2, order, outgoingTotal),
        endY: target.y + NODE_HEIGHT / 2,
      },
    ];
  });

  const runY = assignByGroup(
    routes,
    (route) => route.source.y,
    /*
     * 水路が実際に走る範囲は、始点側のガター（升目の右）から終点側のガター（升目の左）までで、
     * 升目の左端どうしの区間より両側へ広い。狭く取ると、隣り合うだけの線が同じ水路を共有して
     * 実際には数十 px 重なる。両側へガター 1 本ぶんの余裕を足す。
     */
    (route) => ({
      from: Math.min(route.source.x, route.target.x) - GUTTER_WITH_LABEL,
      to:
        Math.max(route.source.x + route.source.cellWidth, route.target.x + route.target.cellWidth) +
        GUTTER_WITH_LABEL,
    }),
    (route, lane, count) => channelY(route.source.y, lane, count),
  );
  const turnX = assignByGroup(
    routes,
    (route) => route.source.x,
    (route, index) => spanOf(route.startY, runY[index]),
    (route, lane, count) => gutterX(route.source.x + route.source.cellWidth, lane, count),
  );
  const riseX = assignByGroup(
    routes,
    (route) => route.target.x,
    (route, index) => spanOf(runY[index], route.endY),
    (route, lane, count) => entryX(route.target.x, lane, count),
  );

  const drawn = routes.map((route, index) => {
    const geometry = {
      startX: route.startX,
      endX: route.endX,
      adjacent: route.adjacent,
      sourceY: route.source.y,
      targetY: route.target.y,
      order: route.order,
      outgoingTotal: route.outgoingTotal,
      turnX: turnX[index],
      runY: runY[index],
      riseX: riseX[index],
    };
    const { transition } = route.edge;
    return {
      route,
      label: edgeLabelOf(transition.label),
      path: buildEdgePath(geometry),
      seed: edgeLabelPosition({
        ...geometry,
        sourceCellRight: route.source.x + route.source.cellWidth,
      }),
      branchColor: branchColorOf(route.order, route.outgoingTotal),
    };
  });

  /*
   * ラベルの位置は図 1 枚ぶんをまとめて解く。1 本ずつ決めると、ほかのラベルと水平な線がどこに
   * 居るかを知らないまま置くことになり、重なりが必ず残る。
   */
  const resolved = new Map(
    resolveEdgeLabelPositions(
      drawn.flatMap((entry) =>
        entry.label === null
          ? []
          : [
              {
                id: entry.route.edge.transition.id,
                label: entry.label,
                x: entry.seed.x,
                y: entry.seed.y,
              },
            ],
      ),
      drawn.map((entry) => entry.path),
      layout.nodes.map((entry) => ({
        left: entry.x,
        right: entry.x + entry.cellWidth,
        top: entry.y,
        bottom:
          entry.y +
          footprintHeightOf(
            entry.item.shape,
            entry.item.flowNode === null ? '' : flowNodeLabelOf(entry.item.flowNode, labels),
          ),
      })),
    ).map((label) => [label.id, label]),
  );

  return drawn.map((entry): Edge => {
    const { transition } = entry.route.edge;
    const position = resolved.get(transition.id) ?? entry.seed;
    const marked = markedTransitionIds.has(transition.id) && markedKind !== 'none';
    const markedClass = !marked
      ? ''
      : markedKind === 'added'
        ? ADDED_EDGE_CLASS
        : REMOVED_EDGE_CLASS;
    return {
      id: transition.id,
      source: entry.route.edge.from,
      target: entry.route.edge.to,
      type: FLOW_EDGE_TYPE,
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
      className: [markedClass, `flow-edge-${transition.destinationKind}`]
        .filter((part) => part !== '')
        .join(' '),
      data: {
        path: entry.path,
        label: entry.label,
        labelX: position.x,
        labelY: position.y,
        branchColor: marked ? null : entry.branchColor,
        ...(transition.destinationKind === 'back' ? { title: backWord } : {}),
        endingId: transition.endingId,
        destinationKind: transition.destinationKind,
      },
    };
  });
}
